import { useCallback } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SectionLoader } from "@/components/ui/loader";
import { useCheckoutReturn } from "@/lib/hooks/use-checkout-return";
import { useProvisioning } from "@/lib/hooks/use-provisioning";
import { clearSignupPlan } from "@/lib/onboarding/signup-plan";
import { PLAN_NAMES } from "@/lib/plan-names";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";
import type { Plan } from "@/lib/api/client";
import { isCheckoutReturn } from "./checkout-return";

/**
 * The four onboarding steps the first run replaced (task 512), kept working
 * for the links, bookmarks and open tabs that still name them: customers
 * have had the URLs since 2026-09-24. Each sends a tenant without a site to
 * the first run and one with a site to the Overview; the layout does the
 * second before any of these renders.
 */
export function LegacyStepRoute() {
  return <Navigate to="/onboarding" replace />;
}

/**
 * `/onboarding/tier`. A checkout started there before the first run
 * shipped comes back here, `?session_id=…`, and is verified as it was
 * (`useCheckoutReturn`), then lands on the Overview, or on the first run for
 * a tenant that had not added its site yet. Anything else is a step that no
 * longer exists.
 */
export function LegacyTierRoute() {
  const { pathname, search } = useLocation();
  if (!isCheckoutReturn(pathname, search)) {
    return <Navigate to="/onboarding" replace />;
  }
  return <LegacyCheckoutReturn />;
}

function LegacyCheckoutReturn() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useProvisioning();
  const hasHadSite = Boolean(profile?.tenant.firstSiteCreatedAt);

  const onward = useCallback(async () => {
    // The plan is paid for now, or was declined: the first run must not
    // send anyone to checkout for it again.
    clearSignupPlan();
    await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    navigate(hasHadSite ? "/dashboard" : "/onboarding", { replace: true });
  }, [hasHadSite, navigate, queryClient]);

  useCheckoutReturn({
    onVerified: useCallback(
      (plan: Exclude<Plan, "free">) => {
        trackEvent(AnalyticsEvents.CHECKOUT_COMPLETED, { tier: plan });
        toast.success(`You're on ${PLAN_NAMES[plan]}.`);
        void onward();
      },
      [onward],
    ),
    onCancelled: useCallback(() => {
      toast.info("Checkout cancelled. You're on the Free plan.");
      void onward();
    }, [onward]),
  });

  return <SectionLoader className="py-16" label="Confirming your plan…" />;
}
